/**
 * Switch: an on/off setting that applies at once (Typewriter scrolling, Sync) (cockatrice-ui `src/ui/Switch.jsx`,
 * ported to hyperscript; PLAN.md §0).
 * A button with role="switch" and aria-checked. Use Checkbox instead when the choice
 * is only saved by a later "Save" button.
 * onChange(checked, event). `stateText` shows "On"/"Off" next to the track (or pass [on, off]).
 */
import './css/switch.css';
import { createSignal, createUniqueId, mergeProps, splitProps } from 'solid-js';
import { useField } from './Field.ts';
import { el, spread, when } from './solid.ts';
import type { Attributes, Child } from './solid.ts';
import { uiText } from './text.ts';

export interface SwitchProps extends Attributes {
  /** Visible label. Without it use aria-label or a Field. */
  readonly label?: Child;
  /** Second line under the label. */
  readonly description?: Child;
  /** Controlled state. */
  readonly checked?: boolean | undefined;
  /** Uncontrolled starting state. */
  readonly defaultChecked?: boolean | undefined;
  /** Called when toggled. */
  readonly onChange?: ((checked: boolean, event: MouseEvent) => void) | undefined;
  /** Show a state word: true for "On"/"Off", or your own [on, off]. */
  readonly stateText?: boolean | readonly [string, string] | undefined;
  /** start: label left, switch right. end: switch first. */
  readonly labelPosition?: 'start' | 'end' | undefined;
  /** Track size. */
  readonly size?: 'sm' | 'md' | undefined;
  /** Disables the switch. */
  readonly disabled?: boolean | undefined;
  /** Extra class. */
  readonly class?: string | undefined;
}

export default function Switch(props: SwitchProps): Child {
  const p = mergeProps({ size: 'md', labelPosition: 'start' } as const, props);
  const [local, rest] = splitProps(p, [
    'id',
    'label',
    'description',
    'checked',
    'defaultChecked',
    'onChange',
    'disabled',
    'size',
    'stateText',
    'labelPosition',
    'class',
    'aria-describedby',
  ]);
  const field = useField();
  const own = createUniqueId();
  const id = (): string => {
    const given = local.id;
    return typeof given === 'string' ? given : (field?.id ?? own);
  };
  const labelId = `${own}-label`;
  const descId = `${own}-desc`;
  const [inner, setInner] = createSignal(local.defaultChecked === true);
  const on = (): boolean => local.checked ?? inner();

  const words = (): string | null => {
    const text = local.stateText;
    if (text === undefined || text === false) return null;
    const [a, b] = text === true ? [uiText()('ui.switch.on'), uiText()('ui.switch.off')] : text;
    return on() ? a : b;
  };

  const toggle = (event: MouseEvent): void => {
    const next = !on();
    setInner(next);
    local.onChange?.(next, event);
  };

  const control = (): Child =>
    el(
      'button',
      spread(rest, {
        id,
        type: 'button',
        role: 'switch',
        class: () => ['ui-switch__control', `ui-switch__control--${local.size}`].join(' '),
        'aria-checked': () => (on() ? 'true' : 'false'),
        'aria-labelledby': () => (local.label ? labelId : rest['aria-labelledby']),
        'aria-describedby': () => {
          const extra = local['aria-describedby'];
          return [field?.describedBy, Boolean(local.description) && descId, typeof extra === 'string' ? extra : undefined].filter(Boolean).join(' ') || undefined;
        },
        disabled: () => local.disabled,
        onClick: toggle,
      }),
      when(
        () => Boolean(words()),
        () => el('span', { class: 'ui-switch__state', 'aria-hidden': 'true' }, words),
      ),
      el('span', { class: 'ui-switch__track', 'aria-hidden': 'true' }, el('span', { class: 'ui-switch__thumb' })),
    );

  const cls = (): string =>
    ['ui-switch', Boolean(local.label) && `ui-switch--label-${local.labelPosition}`, local.disabled === true && 'is-disabled', local.class].filter(Boolean).join(' ');

  return when(
    () => Boolean(local.label),
    () =>
      el(
        'div',
        { class: cls },
        el(
          'span',
          { class: 'ui-switch__text' },
          el('label', { class: 'ui-switch__label', id: labelId, for: id }, () => local.label),
          when(
            () => Boolean(local.description),
            () => el('span', { class: 'ui-switch__description', id: descId }, () => local.description),
          ),
        ),
        control(),
      ),
    () => el('span', { class: cls }, control()),
  );
}
